import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
  FiClipboard,
  FiClock,
  FiInfo,
  FiSearch,
  FiUsers,
} from "react-icons/fi";
import { Navigate, useNavigate } from "react-router-dom";
import http from "../../../api/apiClient";
import { useCan } from "../../../authz/useCan";
import ParentPortalPageTemplate from "../../../components/ParentPortalPageTemplate";
import Modal from "../../../components/Modal";
import Spinner from "../../../components/Spinner";
import { LdsBadge } from "../../../components/lds/LdsBadge";
import { parentSchoolTasksApi } from "../../../features/schoolTasks/api";
import { parseToDate } from "../../../utils/dateUtils";
import type {
  Child,
  ClassroomAssignment,
  ParentCleaningAssignment,
  Family,
  SchoolTask,
  SchoolTaskSignup,
  SchoolTaskStatus,
  SchoolYear,
} from "../../../features/schoolTasks/types";
import SchoolTaskCalendarAction from "./SchoolTaskCalendarAction";
import type { SchoolTaskCalendarItem } from "./schoolTaskCalendarExport";

type View = "overview" | "help" | "contribution";
type HelpFilter = "all" | "classes" | "school" | "ongoing";
type ChildRow = Child & {
  familyId: string;
  classroomId?: string;
  classroomName?: string;
};

const panelClass =
  "rounded-[1.75rem] border border-slate-200/80 bg-white/90 shadow-sm backdrop-blur dark:border-slate-800 dark:bg-slate-950/70";

const formatDate = (value: string | null | undefined, locale: string) => {
  const date = parseToDate(value);
  return date
    ? new Intl.DateTimeFormat(locale, { dateStyle: "full" }).format(date)
    : "";
};

const formatTime = (value: string | null | undefined, locale: string) => {
  const date = parseToDate(value);
  return date
    ? new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
      }).format(date)
    : "";
};

const yearLabel = (year?: SchoolYear) => {
  if (!year?.startDate || !year?.endDate) return "";
  const startDate = parseToDate(year.startDate);
  const endDate = parseToDate(year.endDate);
  return startDate && endDate
    ? `${startDate.getFullYear()}–${endDate.getFullYear()}`
    : "";
};

const schoolTasksPortalUrl = () =>
  typeof window === "undefined"
    ? null
    : `${window.location.origin}/portal?tab=actions&view=school-tasks`;

const parentFacingTaskDescription = (description?: string | null) =>
  (description || "")
    .replace(/(?:^|\s)[^.]*coördinat(?:ie|or)[^.]*\.\s*/gi, " ")
    .replace(/\s*Bron:\s*.*$/i, "")
    .trim();

const taskCalendarItem = (task: SchoolTask): SchoolTaskCalendarItem | null => {
  const startsAt = task.startsAt || task.activity?.startsAt;
  if (!startsAt) return null;
  return {
    id: task.id,
    title: task.name,
    startsAt,
    endsAt: task.activity?.endsAt,
    description: parentFacingTaskDescription(task.description || task.activity?.description),
    url: schoolTasksPortalUrl(),
  };
};

export default function ParentSchoolTasks({ embedded = false }: { embedded?: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const canViewTasks = useCan("school.tasks", "view");
  const canViewSignups = useCan("school.task-signups", "view");
  const canCreateSignupDirect = useCan("school.task-signups", "create");
  const canManageSignups = useCan("school.task-signups", "manage");
  const canAccessSignups = canViewSignups || canManageSignups;
  const canCreateSignup = canCreateSignupDirect || canManageSignups;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingTaskId, setSavingTaskId] = useState("");
  const [year, setYear] = useState<SchoolYear>();
  const [families, setFamilies] = useState<Family[]>([]);
  const [children, setChildren] = useState<ChildRow[]>([]);
  const [tasks, setTasks] = useState<SchoolTask[]>([]);
  const [signups, setSignups] = useState<SchoolTaskSignup[]>([]);
  const [statuses, setStatuses] = useState<SchoolTaskStatus[]>([]);
  const [cleaning, setCleaning] = useState<ParentCleaningAssignment[]>([]);
  const [activeView, setActiveView] = useState<View>("overview");
  const [helpFilter, setHelpFilter] = useState<HelpFilter>("all");
  const [showOtherTasks, setShowOtherTasks] = useState(false);
  const [expandedChild, setExpandedChild] = useState("");
  const [chooser, setChooser] = useState<{
    child: ChildRow | null;
    familyId: string;
    types: string[];
    task?: SchoolTask;
    eligibleChildren?: ChildRow[];
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [selected, familyRes] = await Promise.all([
        parentSchoolTasksApi.schoolYear(),
        http.get<Family[]>("/families/me"),
      ]);
      if (!selected) throw new Error("no-school-year");
      const familyList = Array.isArray(familyRes.data)
        ? familyRes.data
        : [familyRes.data].filter(Boolean);
      const childGroups = await Promise.all(
        familyList.map(async (family) => {
          const childRes = await http.get<Child[]>("/children/mine", {
            params: { familyId: family.id },
          });
          return Promise.all(
            (childRes.data || []).map(async (child) => {
              let classroom: ClassroomAssignment | undefined;
              try {
                classroom = (
                  await http.get<ClassroomAssignment>(
                    `/parent-portal/children/${child.id}/current-class`,
                  )
                ).data;
              } catch {
                // A child without a current classroom remains visible.
              }
              return {
                ...child,
                familyId: family.id,
                classroomId: String(
                  classroom?.classroomId ||
                    classroom?.classroomUuid ||
                    classroom?.classroom?.id ||
                    "",
                ),
                classroomName:
                  classroom?.className || classroom?.classroom?.className,
              };
            }),
          );
        }),
      );
      const [taskData, signupData, statusData, cleaningData] =
        await Promise.all([
          parentSchoolTasksApi.tasks(selected.id),
          canAccessSignups
            ? parentSchoolTasksApi.signups()
            : Promise.resolve([]),
          parentSchoolTasksApi.statuses(selected.id),
          parentSchoolTasksApi.cleaning(selected.id),
        ]);
      setYear(selected);
      setFamilies(familyList);
      setChildren(childGroups.flat());
      setTasks(taskData);
      setSignups(signupData);
      setStatuses(statusData);
      setCleaning(cleaningData);
    } catch {
      setError(
        t("schoolTasks.errors.load", {
          defaultValue: "Could not load school tasks.",
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [canAccessSignups, t]);

  useEffect(() => {
    if (canViewTasks) void load();
  }, [canViewTasks, load]);

  const taskById = useMemo(
    () => new Map(tasks.map((task) => [task.id, task])),
    [tasks],
  );
  const statusByFamily = useMemo(
    () => new Map(statuses.map((status) => [status.familyId, status])),
    [statuses],
  );
  const mandatoryChildren = children.filter((child) => {
    const status = statusByFamily.get(child.familyId);
    return !status?.exempt && Object.prototype.hasOwnProperty.call(
      status?.mandatoryParentTasks || {}, child.id,
    );
  });
  const completedChildren = mandatoryChildren.filter(
    (child) =>
      statusByFamily.get(child.familyId)?.mandatoryParentTasks?.[child.id],
  );
  const familiesWithGeneralRequirement = statuses.filter(
    (status) => !status.exempt,
  );
  const contributionTotal =
    mandatoryChildren.length +
    familiesWithGeneralRequirement.reduce((total, status) => total + status.generalContributionRequiredCount, 0);
  const contributionDone =
    completedChildren.length +
    familiesWithGeneralRequirement.reduce((total, status) => total + Math.min(status.generalContributionCount, status.generalContributionRequiredCount), 0);
  const contributionPercentage = contributionTotal
    ? Math.min(100, (contributionDone / contributionTotal) * 100)
    : 100;
  const ownSignups = signups.filter((signup) => taskById.has(signup.taskId));
  const openTasks = tasks.filter(
    (task) => task.signupCount < task.capacity,
  );

  const eligibleSignupTargets = (task: SchoolTask) => {
    if (task.type === "PARENT_TASK") {
      const candidates = children.filter((candidate) => {
        const status = statusByFamily.get(candidate.familyId);
        const required = Object.prototype.hasOwnProperty.call(
          status?.mandatoryParentTasks || {},
          candidate.id,
        );
        const complete = status?.mandatoryParentTasks?.[candidate.id];
        return (
          !status?.exempt &&
          task.classroomIds.includes(candidate.classroomId || "") &&
          !signups.some(
            (signup) =>
              signup.taskId === task.id && signup.childId === candidate.id,
          ) &&
          (task.optional || (required && !complete))
        );
      });
      return candidates.map((child) => ({ child, familyId: child.familyId }));
    }
    const family = families.find((candidate) => {
      const status = statusByFamily.get(candidate.id);
      // More general contributions are allowed, also for an exempt family; a family can take each task only once.
      return (
        !signups.some(
          (signup) => signup.familyId === candidate.id && signup.taskId === task.id && !signup.childId,
        )
      );
    });
    return family ? [{ child: null, familyId: family.id }] : [];
  };

  const signUp = async (
    task: SchoolTask,
    target = eligibleSignupTargets(task)[0],
  ) => {
    if (!canCreateSignup || !target || savingTaskId) return;
    setSavingTaskId(task.id);
    setError("");
    try {
      await parentSchoolTasksApi.signup(
        task.id,
        target.child?.id || null,
        target.familyId,
      );
      setChooser(null);
      await load();
    } catch (e: any) {
      setError(
        e?.response?.data?.message ||
          t("schoolTasks.errors.save", {
            defaultValue: "Could not save the selection.",
          }),
      );
    } finally {
      setSavingTaskId("");
    }
  };

  const isRegisteredForTask = (task: SchoolTask) =>
    signups.some((signup) => signup.taskId === task.id);
  const canSignUpForTask = (task: SchoolTask) =>
    task.signupCount < task.capacity && eligibleSignupTargets(task).length > 0;

  const filteredHelpTasks = tasks.filter((task) => {
    if (helpFilter === "classes")
      return task.type === "PARENT_TASK" && children.some((child) =>
        task.classroomIds.includes(child.classroomId || ""),
      );
    if (helpFilter === "school") return task.type !== "PARENT_TASK";
    if (helpFilter === "ongoing") return !task.startsAt;
    return true;
  }).sort((a, b) => {
    if (a.activity && !b.activity) return -1;
    if (!a.activity && b.activity) return 1;
    const activityDate = (task: SchoolTask) => task.activity?.startsAt || "9999";
    const taskDate = (task: SchoolTask) => task.startsAt || "9999";
    return activityDate(a).localeCompare(activityDate(b))
      || (a.activity?.name || "").localeCompare(b.activity?.name || "", i18n.language)
      || (a.activity?.id || "").localeCompare(b.activity?.id || "")
      || taskDate(a).localeCompare(taskDate(b))
      || a.name.localeCompare(b.name, i18n.language);
  });
  const shownHelpTasks = filteredHelpTasks.filter(
    (task) => canSignUpForTask(task) || isRegisteredForTask(task),
  );
  const otherHelpTasks = filteredHelpTasks.filter(
    (task) => !canSignUpForTask(task) && !isRegisteredForTask(task),
  );

  const upcoming = [
    ...ownSignups
      .map((signup) => {
        const task = taskById.get(signup.taskId);
        return task?.startsAt
          ? { id: signup.id, startsAt: task.startsAt, task, signup }
          : null;
      })
      .filter(Boolean),
    ...cleaning.map((item) => ({
      id: item.id,
      startsAt: item.startsAt,
      cleaning: item,
    })),
  ]
    .filter((item: any) => (parseToDate(item.startsAt)?.getTime() ?? Number.NaN) >= Date.now())
    .sort(
      (a: any, b: any) =>
        (parseToDate(a.startsAt)?.getTime() ?? Number.NaN) -
        (parseToDate(b.startsAt)?.getTime() ?? Number.NaN),
    )
    .slice(0, 4);

  const withPageTemplate = (content: React.ReactNode) => embedded ? content : (
    <ParentPortalPageTemplate title={t("schoolTasks.title", { defaultValue: "School tasks" })}>
      {content}
    </ParentPortalPageTemplate>
  );

  if (!canViewTasks) return <Navigate to="/403" replace />;
  if (loading && !year)
    return withPageTemplate(<div className="py-20"><Spinner /></div>);
  if (error && !year)
    return withPageTemplate(<div role="alert" className="mx-3 rounded-3xl bg-white p-8 text-center text-slate-900 shadow dark:bg-slate-900 dark:text-white"><p>{error}</p><button onClick={load} className="mt-4 rounded-full bg-emerald-600 px-5 py-3 font-bold text-white">{t("common.retry", { defaultValue: "Try again" })}</button></div>);

  const switchView = (view: View) => {
    setActiveView(view);
    setError("");
  };

  const renderHelpTask = (task: SchoolTask, index: number, list: SchoolTask[]) => {
    const targets = eligibleSignupTargets(task);
    const target = targets[0];
    const registered = isRegisteredForTask(task);
    const classroomNames = children
      .filter((child) =>
        task.classroomIds.includes(child.classroomId || ""),
      )
      .map((child) => child.classroomName)
      .filter(Boolean)
      .filter((name, index, values) => values.indexOf(name) === index);
    const showActivityHeader = Boolean(task.activity && list[index - 1]?.activity?.id !== task.activity.id);
    return (
      <React.Fragment key={task.id}>
      {showActivityHeader && <header className={`${index ? "mt-4" : ""} px-1 pt-2`}><h2 className="text-xl font-black text-slate-950 dark:text-white">{task.activity?.name}</h2>{task.activity?.startsAt && <p className="mt-0.5 text-sm font-bold text-slate-500 dark:text-slate-400">{formatDate(task.activity.startsAt, i18n.language)}</p>}</header>}
      <article className={`${panelClass} overflow-hidden`}>
        <div className="flex items-start gap-3 bg-gradient-to-r from-emerald-50/80 to-white px-5 py-4 dark:from-emerald-950/30 dark:to-slate-950">
          <div className="mt-0.5 rounded-xl bg-white p-2.5 text-emerald-700 shadow-sm dark:bg-slate-900 dark:text-emerald-300">
            <FiUsers />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-black text-slate-950 dark:text-white">{task.name}</h2>
            <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
              {task.startsAt
                ? formatDate(task.startsAt, i18n.language)
                : t("schoolTasks.ongoing", { defaultValue: "Ongoing" })}
              {classroomNames.length
                ? ` · ${classroomNames.join(", ")}`
                : ` · ${t("schoolTasks.schoolWide", { defaultValue: "School-wide" })}`}
            </p>
          </div>
          {task.optional && (
            <LdsBadge tone="info">
              {t("schoolTasks.extra", { defaultValue: "Extra" })}
            </LdsBadge>
          )}
        </div>
        <div className="flex flex-col gap-3 border-t border-slate-100 px-5 py-4 dark:border-slate-800 sm:flex-row sm:items-center">
          <div className="min-w-0 flex-1">
            {parentFacingTaskDescription(task.description) && (
              <p className="font-bold text-slate-800 dark:text-slate-100">{parentFacingTaskDescription(task.description)}</p>
            )}
            <p className={`${parentFacingTaskDescription(task.description) ? "mt-1" : ""} text-sm text-slate-500 dark:text-slate-400`}>
              {t("schoolTasks.placesAvailable", {
                defaultValue: "{{count}} places available",
                count: task.capacity - task.signupCount,
              })}
            </p>
          </div>
          {canCreateSignup && canSignUpForTask(task) ? (
            <button
              type="button"
              disabled={Boolean(savingTaskId)}
              onClick={() => {
                if (targets.length === 1) void signUp(task, target);
                else setChooser({
                  child: null,
                  familyId: "",
                  types: [],
                  task,
                  eligibleChildren: targets.map((item) => item.child).filter(Boolean) as ChildRow[],
                });
              }}
              className="rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-black text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-wait disabled:opacity-60"
            >
              {savingTaskId === task.id
                ? t("schoolTasks.signingUp", { defaultValue: "Signing up…" })
                : t("schoolTasks.offerHelp", { defaultValue: "I can help" })}
            </button>
          ) : registered ? (
            <LdsBadge tone="success" className="gap-1.5 self-start py-1.5 sm:self-auto">
              <FiCheckCircle />
              {t("schoolTasks.registered", {
                defaultValue: "Registered",
              })}
            </LdsBadge>
          ) : (
            <LdsBadge tone="neutral" className="gap-1.5 self-start py-1.5 sm:self-auto">
              <FiCheck />
              {t("schoolTasks.notEligible", { defaultValue: "Not available for your family" })}
            </LdsBadge>
          )}
        </div>
      </article>
      </React.Fragment>
    );
  };

  return withPageTemplate(
      <div className={`mx-auto max-w-5xl pb-10 ${embedded ? "px-0" : "px-3 sm:px-0"}`}>
        <header className="mb-5 flex flex-wrap items-center gap-3">
          {!embedded && <button
            onClick={() => navigate("/portal")}
            aria-label={t("common.back", { defaultValue: "Back" })}
            className="rounded-full border border-white/80 bg-white/80 p-3 text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
          >
            <FiChevronLeft />
          </button>}
          <div className="min-w-0 flex-1">
            <h1 className="text-2xl font-black text-slate-950 dark:text-white">
              {activeView === "help"
                ? t("schoolTasks.helpWanted", { defaultValue: "Help wanted" })
                : activeView === "contribution"
                  ? t("schoolTasks.ourContribution", {
                      defaultValue: "Our contribution",
                    })
                  : t("schoolTasks.title", { defaultValue: "School tasks" })}
            </h1>
            <p className="mt-0.5 text-sm font-medium text-slate-500 dark:text-slate-400">
              {activeView === "help"
                ? t("schoolTasks.helpWantedSubtitle", {
                    defaultValue: "Everyone can help. Together we make it possible.",
                  })
                : activeView === "contribution"
                  ? t("schoolTasks.contributionSubtitle", {
                      defaultValue: "See your family's contribution for this school year.",
                    })
                  : t("schoolTasks.overviewSubtitle", {
                      defaultValue: "Together we support our school. Thank you for helping!",
                    })}
            </p>
          </div>
          {yearLabel(year) && (
            <span className="rounded-xl border border-slate-200 bg-white/80 px-4 py-2 text-sm font-bold text-slate-700 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
              {t("schoolTasks.schoolYear", { defaultValue: "School year" })}{" "}
              {yearLabel(year)}
            </span>
          )}
        </header>

        <nav
          aria-label={t("schoolTasks.parentSections", {
            defaultValue: "School task sections",
          })}
          className="mb-5 flex gap-1 overflow-x-auto border-b border-slate-200 dark:border-slate-700"
        >
          {([
            ["overview", t("schoolTasks.overview", { defaultValue: "Overview" })],
            ["help", t("schoolTasks.helpWanted", { defaultValue: "Help wanted" })],
            ["contribution", t("schoolTasks.ourContribution", { defaultValue: "Our contribution" })],
          ] as [View, string][]).map(([id, label]) => (
            <button
              key={id}
              type="button"
              aria-current={activeView === id ? "page" : undefined}
              onClick={() => switchView(id)}
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-bold transition ${
                activeView === id
                  ? "border-emerald-600 text-emerald-700 dark:text-emerald-300"
                  : "border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
              }`}
            >
              {label}
            </button>
          ))}
        </nav>

        {error && (
          <div role="alert" className="mb-5 rounded-2xl bg-rose-50 p-4 text-sm font-bold text-rose-700 dark:bg-rose-950/30 dark:text-rose-200">
            {error}
          </div>
        )}

        {activeView === "overview" && (
          <Overview
            contributionDone={contributionDone}
            contributionTotal={contributionTotal}
            percentage={contributionPercentage}
            scheduledCount={
              ownSignups.filter((signup) => {
                const startsAt = taskById.get(signup.taskId)?.startsAt;
                return startsAt && (parseToDate(startsAt)?.getTime() ?? Number.NaN) >= Date.now();
              }).length + cleaning.filter((item) => (parseToDate(item.startsAt)?.getTime() ?? Number.NaN) >= Date.now()).length
            }
            openPlaces={openTasks
              .filter((task) => eligibleSignupTargets(task).length > 0)
              .reduce(
              (sum, task) => sum + task.capacity - task.signupCount,
              0,
            )}
            upcoming={upcoming as any[]}
            children={children}
            locale={i18n.language}
            onView={switchView}
            t={t}
          />
        )}

        {activeView === "help" && (
          <div className="grid gap-4">
            <div className="flex gap-2 overflow-x-auto pb-1">
              {([
                ["all", t("schoolTasks.filters.all", { defaultValue: "All" })],
                ["classes", t("schoolTasks.filters.myClasses", { defaultValue: "My classes" })],
                ["school", t("schoolTasks.filters.schoolWide", { defaultValue: "School-wide" })],
                ["ongoing", t("schoolTasks.filters.ongoing", { defaultValue: "Ongoing" })],
              ] as [HelpFilter, string][]).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={helpFilter === id}
                  onClick={() => setHelpFilter(id)}
                  className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-bold transition ${
                    helpFilter === id
                      ? "border-emerald-600 bg-emerald-600 text-white"
                      : "border-slate-200 bg-white/75 text-slate-600 hover:border-emerald-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            {shownHelpTasks.length ? (
              shownHelpTasks.map((task, index) => renderHelpTask(task, index, shownHelpTasks))
            ) : (
              <EmptyState icon={<FiSearch />} text={t("schoolTasks.noAvailable", { defaultValue: "No available tasks." })} />
            )}
            {otherHelpTasks.length > 0 && (
              <>
                <button
                  type="button"
                  aria-expanded={showOtherTasks}
                  aria-controls="school-tasks-other-tasks"
                  onClick={() => setShowOtherTasks((open) => !open)}
                  className="inline-flex items-center gap-2 justify-self-center rounded-full border border-slate-200 bg-white/75 px-4 py-2 text-sm font-bold text-slate-600 transition hover:border-emerald-300 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                >
                  {showOtherTasks
                    ? t("schoolTasks.hideOtherTasks", { defaultValue: "Hide other tasks" })
                    : t("schoolTasks.showOtherTasks", { defaultValue: "Show other tasks ({{count}})", count: otherHelpTasks.length })}
                  <FiChevronDown aria-hidden="true" className={`transition ${showOtherTasks ? "rotate-180" : ""}`} />
                </button>
                {showOtherTasks && (
                  <div id="school-tasks-other-tasks" className="grid gap-4">
                    {otherHelpTasks.map((task, index) => renderHelpTask(task, index, otherHelpTasks))}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeView === "contribution" && (
          <div className="grid gap-5">
            <section className="grid gap-4 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
              <div className={`${panelClass} p-5`}>
                <h2 className="text-sm font-black text-slate-700 dark:text-slate-200">
                  {t("schoolTasks.progress", { defaultValue: "Progress" })}
                </h2>
                <p className="mt-3 text-2xl font-black text-slate-950 dark:text-white">
                  {t("schoolTasks.taskCount", {
                    defaultValue: "{{done}} of {{total}} tasks",
                    done: contributionDone,
                    total: contributionTotal,
                  })}
                </p>
                <Progress value={contributionPercentage} />
              </div>
              <div className={`${panelClass} p-5`}>
                <ul className="grid gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
                  <Rule complete={completedChildren.length === mandatoryChildren.length} text={t("schoolTasks.rules.parentTask", { defaultValue: "At least one parent task for each relevant child" })} />
                  <Rule complete={statuses.every((status) => status.generalContributionComplete || status.exempt)} text={t("schoolTasks.rules.general", { defaultValue: "A general contribution or holiday cleaning" })} />
                  <Rule complete text={t("schoolTasks.rules.cleaning", { defaultValue: "Cleaning moments are scheduled by the school" })} />
                  <Rule complete text={t("schoolTasks.rules.optional", { defaultValue: "Optional tasks are extra" })} />
                </ul>
              </div>
            </section>

            <Section title={t("schoolTasks.perChild", { defaultValue: "Tasks per child" })}>
              <div className="divide-y divide-slate-100 dark:divide-slate-800">
                {children.length ? children.map((child, index) => {
                  const childSignups = ownSignups.filter(
                    (signup) => signup.childId === child.id,
                  );
                  const required = mandatoryChildren.some(
                    (item) => item.id === child.id && item.familyId === child.familyId,
                  );
                  const complete = Boolean(
                    statusByFamily.get(child.familyId)?.mandatoryParentTasks?.[child.id],
                  );
                  const expanded = expandedChild === `${child.familyId}-${child.id}`;
                  const childExempt = Boolean(statusByFamily.get(child.familyId)?.exempt);
                  const fullName = [child.firstName, child.lastName].filter(Boolean).join(" ") || t("schoolTasks.unknownChild", { defaultValue: "Child" });
                  return (
                    <div key={`${child.familyId}-${child.id}`}>
                      <button
                        type="button"
                        aria-expanded={expanded}
                        onClick={() => setExpandedChild(expanded ? "" : `${child.familyId}-${child.id}`)}
                        className="grid w-full grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-3 px-4 py-3 text-left transition hover:bg-slate-50 dark:hover:bg-slate-900/60"
                      >
                        <span className={`grid h-9 w-9 place-items-center rounded-full text-sm font-black text-white ${["bg-rose-400", "bg-emerald-500", "bg-blue-400", "bg-violet-400", "bg-amber-400"][index % 5]}`}>
                          {(child.firstName || "?").slice(0, 1).toUpperCase()}
                        </span>
                        <span className="min-w-0">
                          <span className="block truncate font-black text-slate-900 dark:text-white">{fullName}</span>
                          <span className="block truncate text-xs font-medium text-slate-500">{child.classroomName || t("schoolTasks.noClass", { defaultValue: "No class yet" })}</span>
                        </span>
                        <LdsBadge tone={childExempt ? "neutral" : complete ? "success" : required ? "warning" : "neutral"}>
                          {childExempt
                            ? t("schoolTasks.exempt", { defaultValue: "Exempt" })
                            : required
                            ? t("schoolTasks.childTaskCount", { defaultValue: "{{count}} task", count: childSignups.filter((signup) => taskById.get(signup.taskId)?.type === "PARENT_TASK").length })
                            : t("schoolTasks.none", { defaultValue: "None" })}
                        </LdsBadge>
                        <FiChevronDown className={`transition ${expanded ? "rotate-180" : ""}`} />
                      </button>
                      {expanded && (
                        <div className="grid gap-2 bg-slate-50/70 px-4 py-3 pl-16 dark:bg-slate-900/50">
                          {childExempt ? <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300">{t("schoolTasks.exempt", { defaultValue: "Exempt" })}</p> : childSignups.length ? childSignups.map((signup) => {
                            const task = taskById.get(signup.taskId);
                            return task ? (
                              <div key={signup.id} className="flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
                                <div><p className="font-bold text-slate-800 dark:text-slate-100">{task.name}</p>
                                {task.startsAt && <p className="text-slate-500">{formatDate(task.startsAt, i18n.language)}</p>}</div>
                                {taskCalendarItem(task) && <SchoolTaskCalendarAction item={taskCalendarItem(task)!} />}
                              </div>
                            ) : null;
                          }) : (
                            <p className="text-sm text-slate-500">{required ? t("schoolTasks.noTaskSelected", { defaultValue: "No parent task selected" }) : t("schoolTasks.noTaskNeeded", { defaultValue: "No parent task required" })}</p>
                          )}
                          {canCreateSignup && required && !complete && (
                            <button type="button" onClick={() => setChooser({ child, familyId: child.familyId, types: ["PARENT_TASK"] })} className="mt-1 justify-self-start rounded-full bg-emerald-600 px-4 py-2 text-sm font-black text-white">
                              {t("schoolTasks.chooseTask", { defaultValue: "Choose task" })}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  );
                }) : <p className="p-5 text-sm font-medium text-slate-500">{t("schoolTasks.noChildren", { defaultValue: "No children found." })}</p>}
              </div>
            </Section>

            <Section title={t("schoolTasks.generalContribution", { defaultValue: "General contribution" })}>
              <div className="grid gap-3 p-4 sm:grid-cols-2">
                {families.length ? families.map((family) => {
                  const status = statusByFamily.get(family.id);
                  const selected = ownSignups.map((signup) => signup.familyId === family.id && !signup.childId ? taskById.get(signup.taskId) : undefined).filter(Boolean) as SchoolTask[];
                  return (
                    <div key={family.id} className="rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                      <p className="font-black text-slate-900 dark:text-white">{family.familyName || t("schoolTasks.family", { defaultValue: "Family" })}</p>
                      {status?.exempt
                        ? <p className="mt-2 text-sm font-bold text-emerald-700 dark:text-emerald-300">{t("schoolTasks.exempt", { defaultValue: "Exempt" })}</p>
                        : status && <p className="mt-1 text-sm font-bold text-slate-600 dark:text-slate-300">{t("schoolTasks.generalContributionProgress", { defaultValue: "{{count}} of {{required}} chosen", count: status.generalContributionCount, required: status.generalContributionRequiredCount })}</p>}
                      {selected.map((task) => <div key={task.id} className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"><p className="text-sm font-bold text-emerald-700 dark:text-emerald-300">{task.name}{task.optional && <span className="ml-2 text-xs font-medium text-slate-500">({t("schoolTasks.extra", { defaultValue: "Extra" })})</span>}</p>{taskCalendarItem(task) && <SchoolTaskCalendarAction item={taskCalendarItem(task)!} />}</div>)}
                      {!status?.generalContributionComplete && <p className="mt-2 text-sm font-medium text-amber-700 dark:text-amber-200">{t("schoolTasks.contributionNeeded", { defaultValue: "A choice is still needed" })}</p>}
                      {canCreateSignup && <button type="button" onClick={() => setChooser({ child: null, familyId: family.id, types: ["GENERAL_TASK", "HOLIDAY_CLEANING"] })} className="mt-3 rounded-full border border-emerald-600 px-4 py-2 text-sm font-black text-emerald-700 dark:text-emerald-300">{status?.generalContributionComplete ? t("schoolTasks.chooseExtraContribution", { defaultValue: "Choose an extra contribution" }) : t("schoolTasks.chooseContribution", { defaultValue: "Choose contribution" })}</button>}
                    </div>
                  );
                }) : <p className="sm:col-span-2 text-sm font-medium text-slate-500">{t("schoolTasks.noFamilies", { defaultValue: "No families found." })}</p>}
              </div>
            </Section>

            <Section title={t("schoolTasks.weeklyCleaning", { defaultValue: "Weekly cleaning" })}>
              <div className="p-4">
                <p className="mb-3 flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400"><FiInfo />{t("schoolTasks.cleaningReadOnly", { defaultValue: "Cleaning moments are assigned by the school." })}</p>
                <div className="grid gap-2">
                  {cleaning.length ? cleaning.map((item) => {
                    const child = children.find((candidate) => candidate.id === item.childId);
                    return (
                      <div key={item.id} className="flex flex-col gap-2 rounded-2xl bg-slate-50 p-4 dark:bg-slate-900 sm:flex-row sm:items-center sm:justify-between">
                        <div><p className="font-black text-slate-900 dark:text-white">{formatDate(item.startsAt, i18n.language)} · {formatTime(item.startsAt, i18n.language)}</p><p className="text-sm text-slate-500">{[child?.classroomName, child?.firstName].filter(Boolean).join(" · ")}</p></div>
                        <div className="flex flex-wrap items-center gap-2">{item.description && <span className="text-sm font-bold text-slate-600 dark:text-slate-300">{item.description}</span>}<SchoolTaskCalendarAction item={{ id: item.id, title: t("schoolTasks.cleaning", { defaultValue: "Cleaning" }), startsAt: item.startsAt, description: item.description, url: schoolTasksPortalUrl() }} /></div>
                      </div>
                    );
                  }) : <p className="rounded-2xl bg-slate-50 p-5 text-sm text-slate-500 dark:bg-slate-900">{t("schoolTasks.noCleaning", { defaultValue: "No cleaning moments scheduled." })}</p>}
                </div>
              </div>
            </Section>
          </div>
        )}

        {chooser && (
          <ChoiceDialog
            chooser={chooser}
            tasks={tasks}
            signups={signups}
            savingTaskId={savingTaskId}
            locale={i18n.language}
            onChoose={(task, child) => void signUp(task, { child: child || chooser.child, familyId: child?.familyId || chooser.familyId })}
            onClose={() => setChooser(null)}
            t={t}
          />
        )}
      </div>
  );
}

function Overview({ contributionDone, contributionTotal, percentage, scheduledCount, openPlaces, upcoming, children, locale, onView, t }: any) {
  const remaining = Math.max(0, contributionTotal - contributionDone);
  return (
    <div className="grid gap-5">
      <section className="grid gap-3 md:grid-cols-3">
        <DashboardCard tone="emerald" icon={<FiClipboard />} title={t("schoolTasks.yourContribution", { defaultValue: "Your contribution" })} value={t("schoolTasks.taskCount", { defaultValue: "{{done}} of {{total}} tasks", done: contributionDone, total: contributionTotal })} detail={remaining ? t("schoolTasks.tasksRemaining", { defaultValue: "{{count}} tasks left to choose", count: remaining }) : t("schoolTasks.complete", { defaultValue: "Complete" })} onClick={() => onView("contribution")}>
          <Progress value={percentage} />
        </DashboardCard>
        <DashboardCard tone="sky" icon={<FiClock />} title={t("schoolTasks.yourPlanning", { defaultValue: "Your planning" })} value={t("schoolTasks.scheduledCount", { defaultValue: "{{count}} scheduled", count: scheduledCount })} detail={t("schoolTasks.upcomingHelp", { defaultValue: "Upcoming moments" })} onClick={() => onView("contribution")} />
        <DashboardCard tone="violet" icon={<FiSearch />} title={t("schoolTasks.helpWanted", { defaultValue: "Help wanted" })} value={t("schoolTasks.openPlaces", { defaultValue: "{{count}} open places", count: openPlaces })} detail={t("schoolTasks.whereCanYouHelp", { defaultValue: "Where can you help?" })} onClick={() => onView("help")} />
      </section>
      <Section title={t("schoolTasks.yourUpcomingTasks", { defaultValue: "Your upcoming tasks" })} action={<button type="button" onClick={() => onView("contribution")} className="inline-flex items-center gap-1 text-sm font-bold text-emerald-700 dark:text-emerald-300">{t("schoolTasks.viewAll", { defaultValue: "View all" })}<FiChevronRight /></button>}>
        <div className="grid gap-3 p-4 sm:grid-cols-2">
          {upcoming.length ? upcoming.map((item: any) => {
            const isCleaning = Boolean(item.cleaning);
            const child = children.find((candidate: ChildRow) => candidate.id === (item.cleaning?.childId || item.signup?.childId));
            return (
              <article key={`${isCleaning ? "cleaning" : "task"}-${item.id}`} className="rounded-2xl border border-slate-200 p-4 dark:border-slate-700">
                <div className="flex items-start gap-3"><span className="rounded-xl bg-emerald-50 p-2.5 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">{isCleaning ? <FiCalendar /> : <FiUsers />}</span><div className="min-w-0 flex-1"><h3 className="font-black text-slate-900 dark:text-white">{isCleaning ? t("schoolTasks.cleaning", { defaultValue: "Cleaning" }) : item.task.name}</h3><p className="mt-1 text-sm text-slate-500">{formatDate(item.startsAt, locale)}{isCleaning && ` · ${formatTime(item.startsAt, locale)}`}</p>{isCleaning ? <p className="mt-1 text-sm text-slate-500">{[child?.classroomName, child?.firstName, item.cleaning.description].filter(Boolean).join(" · ")}</p> : parentFacingTaskDescription(item.task.description) && <p className="mt-1 text-sm text-slate-500">{parentFacingTaskDescription(item.task.description)}</p>}</div><LdsBadge tone="success" className="shrink-0">{t("schoolTasks.scheduled", { defaultValue: "Scheduled" })}</LdsBadge></div>
                <div className="mt-3 flex justify-end"><SchoolTaskCalendarAction item={isCleaning ? { id: item.id, title: t("schoolTasks.cleaning", { defaultValue: "Cleaning" }), startsAt: item.startsAt, description: item.cleaning.description, url: schoolTasksPortalUrl() } : taskCalendarItem(item.task)!} /></div>
              </article>
            );
          }) : <div className="sm:col-span-2"><EmptyState icon={<FiCalendar />} text={t("schoolTasks.noUpcomingTasks", { defaultValue: "No upcoming tasks yet." })} /></div>}
        </div>
      </Section>
    </div>
  );
}

function DashboardCard({ tone, icon, title, value, detail, onClick, children }: any) {
  const tones: Record<string, string> = { emerald: "border-emerald-100 bg-emerald-50/75 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-950/25 dark:text-emerald-300", sky: "border-sky-100 bg-sky-50/75 text-sky-700 dark:border-sky-900/50 dark:bg-sky-950/25 dark:text-sky-300", violet: "border-violet-100 bg-violet-50/75 text-violet-700 dark:border-violet-900/50 dark:bg-violet-950/25 dark:text-violet-300" };
  return <button type="button" onClick={onClick} className={`min-h-[9.5rem] rounded-[1.5rem] border p-5 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${tones[tone]}`}><div className="flex items-center gap-2 text-sm font-black">{icon}{title}</div><p className="mt-4 text-xl font-black text-slate-950 dark:text-white">{value}</p>{children}<p className="mt-2 text-sm font-bold">{detail}</p></button>;
}

function Progress({ value }: { value: number }) {
  return <div role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(value)} className="mt-3 h-2 overflow-hidden rounded-full bg-slate-200/80 dark:bg-slate-700"><div className="h-full rounded-full bg-emerald-600 transition-all" style={{ width: `${Math.max(0, Math.min(100, value))}%` }} /></div>;
}

function Rule({ complete, text }: { complete: boolean; text: string }) {
  return <li className="flex items-start gap-2"><span className={`mt-0.5 ${complete ? "text-emerald-600" : "text-slate-400"}`}>{complete ? <FiCheckCircle /> : <FiClock />}</span><span>{text}</span></li>;
}

function EmptyState({ icon, text }: { icon: React.ReactNode; text: string }) {
  return <div className={`${panelClass} grid place-items-center gap-2 p-8 text-center text-slate-500`}><span className="text-2xl text-emerald-600">{icon}</span><p className="font-medium">{text}</p></div>;
}

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return <section className={`${panelClass} overflow-hidden`}><div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800"><h2 className="text-lg font-black text-slate-950 dark:text-white">{title}</h2>{action}</div>{children}</section>;
}

function ChoiceDialog({ chooser, tasks, signups, savingTaskId, locale, onChoose, onClose, t }: any) {
  const choices = tasks.filter((task: SchoolTask) => chooser.types.includes(task.type) && task.signupCount < task.capacity && !signups.some((signup: SchoolTaskSignup) => signup.taskId === task.id && signup.familyId === chooser.familyId && (chooser.child ? signup.childId === chooser.child.id : !signup.childId)) && (!chooser.child || task.classroomIds.includes(chooser.child.classroomId || "")));
  const choosingChild = chooser.task && chooser.eligibleChildren?.length;
  const title = choosingChild ? t("schoolTasks.chooseChild", { defaultValue: "Choose a child" }) : t("schoolTasks.availableChoices", { defaultValue: "Available choices" });
  return <Modal show title={title} onClose={onClose} variant="lds" showIcon={false} hideDefaultActions dialogClassName="max-h-[85vh] sm:max-w-xl"><div className="grid gap-3">{choosingChild ? chooser.eligibleChildren.map((child: ChildRow) => <button key={`${child.familyId}-${child.id}`} type="button" onClick={() => onChoose(chooser.task, child)} className="rounded-2xl border border-slate-200 p-4 text-left font-black hover:border-emerald-500 dark:border-slate-700">{[child.firstName, child.lastName].filter(Boolean).join(" ")}<span className="ml-2 text-sm font-medium text-slate-500">{child.classroomName}</span></button>) : choices.length ? choices.map((task: SchoolTask) => <button key={task.id} type="button" disabled={Boolean(savingTaskId)} onClick={() => onChoose(task)} className="rounded-2xl border border-slate-200 p-4 text-left transition hover:border-emerald-500 disabled:opacity-50 dark:border-slate-700"><span className="font-black">{task.name}</span>{task.optional && <LdsBadge tone="info" className="ml-2 px-2">{t("schoolTasks.extra", { defaultValue: "Extra" })}</LdsBadge>}<p className="mt-1 text-sm text-slate-500">{task.startsAt ? formatDate(task.startsAt, locale) : t("schoolTasks.ongoing", { defaultValue: "Ongoing" })} · {t("schoolTasks.placesAvailable", { defaultValue: "{{count}} places available", count: task.capacity - task.signupCount })}</p></button>) : <p className="rounded-2xl bg-slate-50 p-4 text-slate-500 dark:bg-slate-800">{t("schoolTasks.noAvailable", { defaultValue: "No available tasks." })}</p>}</div><button type="button" onClick={onClose} className="mt-5 w-full rounded-full border border-slate-200 px-4 py-3 font-bold dark:border-slate-700">{t("common.cancel", { defaultValue: "Cancel" })}</button></Modal>;
}
